import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Typography, Card, Space, Select, Checkbox } from 'antd';
import { UserOutlined, ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { putNurseProfile } from '../../../api';
import { useToast } from '../../../ui/ToastProvider';

const ShiftSelector = ({ value = [], onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    {[
      { label: 'Night', value: 'night', desc: 'Overnight shifts' },
      { label: 'Day', value: 'day', desc: 'Standard day shifts' },
      { label: 'Mid-Day', value: 'mid-day', desc: 'Afternoon / Evening' },
    ].map((option) => {
      const isSelected = value.includes(option.value);
      return (
        <div
          key={option.value}
          onClick={() => {
            const newValue = isSelected
              ? value.filter((v) => v !== option.value)
              : [...value, option.value];
            onChange?.(newValue);
          }}
          style={{
            border: isSelected ? '2px solid #004d54' : '1px solid #E4E7EC',
            borderRadius: 12,
            padding: '16px',
            cursor: 'pointer',
            backgroundColor: isSelected ? '#f2fffa' : '#ffffff',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 16, color: isSelected ? '#004d54' : '#101828' }}>{option.label}</span>
          <span style={{ fontSize: 14, color: '#667085' }}>{option.desc}</span>
        </div>
      );
    })}
  </div>
);

const PersonalInfoStep = ({ onNext, onBack, initial, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dobYear, setDobYear] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const { notify } = useToast();
  const [isFormValid, setIsFormValid] = useState(false);
  const [dobError, setDobError] = useState('');
  const [dobTouched, setDobTouched] = useState(false);
  const requiredFields = ['firstName', 'lastName', 'dob', 'shiftPreferences'];
  const MINIMUM_AGE_YEARS = 18;

  const recomputeFormValidity = () => {
    const values = form.getFieldsValue(requiredFields);
    const hasValues = requiredFields.every((k) => !!values[k]);
    const hasErrors = form
      .getFieldsError(requiredFields)
      .some(({ errors }) => (errors || []).length > 0);
    setIsFormValid(hasValues && !hasErrors);
  };
  useEffect(() => {
    if (initial) {
      form.setFieldsValue({
        firstName: initial.firstName ? initial.firstName.trim() : undefined,
        lastName: initial.lastName ? initial.lastName.trim() : undefined,
        dob: initial.dob ? dayjs(initial.dob) : undefined,
        shiftPreferences: initial.shiftPreferences || [],
      });
      const hasAll = !!(initial.firstName && initial.lastName && initial.dob && initial.shiftPreferences?.length);
      setIsFormValid(hasAll);
      // seed DOB segmented fields
      if (initial.dob) {
        const d = dayjs(initial.dob);
        if (d.isValid()) {
          setDobYear(String(d.year()));
          setDobMonth(String(d.month() + 1).padStart(2, '0'));
          setDobDay(String(d.date()).padStart(2, '0'));
        }
      } else {
        setDobYear('');
        setDobMonth('');
        setDobDay('');
      }
      setDobError('');
      setDobTouched(false);
      recomputeFormValidity();
      const d2 = initial?.dob ? dayjs(initial.dob) : null;
      if (d2 && d2.isValid()) {
        const yStr = String(d2.year());
        const mStr = String(d2.month() + 1).padStart(2, '0');
        const dStr = String(d2.date()).padStart(2, '0');
        updateDobInForm(yStr, mStr, dStr);
      } else {
        updateDobInForm('', '', '');
      }
    } else {
      setIsFormValid(false);
    }
  }, [initial]);

  // Keep segmented DOB in sync when initial changes (e.g., navigating back)
  useEffect(() => {
    const d = initial?.dob ? dayjs(initial.dob) : null;
    if (d && d.isValid()) {
      setDobYear(String(d.year()));
      setDobMonth(String(d.month() + 1).padStart(2, '0'));
      setDobDay(String(d.date()).padStart(2, '0'));
    } else {
      setDobYear('');
      setDobMonth('');
      setDobDay('');
    }
    updateDobInForm(
      d && d.isValid() ? String(d.year()) : '',
      d && d.isValid() ? String(d.month() + 1).padStart(2, '0') : '',
      d && d.isValid() ? String(d.date()).padStart(2, '0') : ''
    );
  }, [initial?.dob]);

  const clampNumeric = (value, maxLength) => {
    const digitsOnly = (value || '').replace(/\D/g, '');
    return digitsOnly.slice(0, maxLength);
  };

  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    const y = Number(year);
    const m = Number(month);
    if (Number.isNaN(y) || Number.isNaN(m)) return 31;
    return new Date(y, m, 0).getDate(); // month is 1-based here
  };

  const updateDobInForm = (y, m, d) => {
    const yLen = y?.length || 0;
    const mLen = m?.length || 0;
    const dLen = d?.length || 0;
    const anyFilled = yLen > 0 || mLen > 0 || dLen > 0;

    if (yLen === 4 && mLen >= 1 && dLen >= 1) {
      const mm = m.padStart(2, '0');
      const dd = d.padStart(2, '0');
      const yearNum = Number(y);
      const monthNum = Number(mm);
      const dayNum = Number(dd);
      const maxDay = getDaysInMonth(yearNum, monthNum);
      const valid =
        yearNum >= 1900 && yearNum <= new Date().getFullYear() &&
        monthNum >= 1 && monthNum <= 12 &&
        dayNum >= 1 && dayNum <= maxDay;
      if (valid) {
        const dobDate = dayjs(`${y}-${mm}-${dd}`);
        const isOldEnough = dayjs().diff(dobDate, 'year') >= MINIMUM_AGE_YEARS;
        if (isOldEnough) {
          form.setFieldsValue({ dob: dobDate });
          setDobError('');
          recomputeFormValidity();
          return;
        } else {
          form.setFieldsValue({ dob: undefined });
          setDobError(`You must be at least ${MINIMUM_AGE_YEARS} years old`);
          recomputeFormValidity();
          return;
        }
      }
    }

    // Not valid yet
    form.setFieldsValue({ dob: undefined });
    if (dobTouched || anyFilled) {
      let msg = '';
      if (yLen === 0 && mLen === 0 && dLen === 0) {
        msg = 'Date of birth is required';
      } else if (yLen < 4 || mLen === 0 || dLen === 0) {
        msg = 'Enter complete date of birth';
      } else {
        msg = 'Enter a valid date';
      }
      setDobError(msg);
    } else {
      setDobError('');
    }
    recomputeFormValidity();
  };

  const handleYearChange = (e) => {
    const v = clampNumeric(e.target.value, 4);
    setDobYear(v);
    setDobTouched(true);
    if (v.length === 4 && monthRef.current) {
      monthRef.current.focus();
    }
    updateDobInForm(v, dobMonth, dobDay);
  };

  const handleMonthChange = (e) => {
    const vRaw = clampNumeric(e.target.value, 2);
    setDobMonth(vRaw);
    setDobTouched(true);
    if (vRaw.length === 2 && dayRef.current) {
      dayRef.current.focus();
    }
    updateDobInForm(dobYear, vRaw, dobDay);
  };

  const handleDayChange = (e) => {
    const vRaw = clampNumeric(e.target.value, 2);
    setDobDay(vRaw);
    setDobTouched(true);
    updateDobInForm(dobYear, dobMonth, vRaw);
  };

  const handleMonthBlur = () => {
    if (dobMonth && dobMonth.length === 1) {
      const padded = dobMonth.padStart(2, '0');
      setDobMonth(padded);
      updateDobInForm(dobYear, padded, dobDay);
    }
  };

  const handleDayBlur = () => {
    if (dobDay && dobDay.length === 1) {
      const padded = dobDay.padStart(2, '0');
      setDobDay(padded);
      updateDobInForm(dobYear, dobMonth, padded);
    }
  };

  const handleFinishFailed = () => {
    setDobTouched(true);
    updateDobInForm(dobYear, dobMonth, dobDay);
  };

  const handleYearKeyDown = (e) => {
    if (e.key === 'Backspace' && !dobYear && yearRef.current) {
      // stay
    }
  };

  const handleMonthKeyDown = (e) => {
    if (e.key === 'Backspace' && !dobMonth && yearRef.current) {
      yearRef.current.focus();
    }
  };

  const handleDayKeyDown = (e) => {
    if (e.key === 'Backspace' && !dobDay && monthRef.current) {
      monthRef.current.focus();
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        firstName: values.firstName ? values.firstName.trim() : values.firstName,
        lastName: values.lastName ? values.lastName.trim() : values.lastName,
        dob: values.dob ? values.dob.format('YYYY-MM-DD') : undefined,
        shiftPreferences: values.shiftPreferences || [],
      };
      await putNurseProfile(payload);
      if (onSaved) onSaved(payload);
      notify({ title: 'Saved', description: 'Your personal details were updated.', type: 'success' });
      onNext();
    } catch (e) {
      notify({ title: 'Failed to save', description: 'Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onValuesChange = async () => {
    recomputeFormValidity();
  };

  const getPrimaryBtnStyle = (disabled) => ({
    width: '80%',
    height: 60,
    fontSize: 18,
    ...(disabled
      ? { opacity: 0.6, cursor: 'not-allowed' }
      : { backgroundColor: '#004d54', borderColor: '#004d54', boxShadow: '0 6px 16px rgba(0,77,84,0.24)' })
  });

  return (
    <Card style={{ border: 'none', padding: 0, margin: 0, }} >
      <style>{`
        .onboarding-picker-lg { height: 60px; }
        .onboarding-picker-lg .ant-picker-input { height: 60px; }
        .onboarding-picker-lg .ant-picker-input > input { height: 60px; }
        .onboarding-select-lg .ant-select-selector { min-height: 60px !important; padding: 12px !important; }
        .onboarding-select-lg .ant-select-selection-overflow { align-items: center; }
      `}</style>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Personal details</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Tell us who you are. Use your legal name as it appears on your government ID.
        </Typography.Paragraph>
      </Space>

      <Form form={form} layout="vertical" onFinish={handleSubmit} onFinishFailed={handleFinishFailed} onValuesChange={onValuesChange}>
        <Form.Item name="firstName" label="First name" rules={[{ required: true, message: 'First name is required' }]}>
          <Input size="large" allowClear prefix={<UserOutlined style={{ color: '#8c8c8c' }} />} placeholder="e.g. Jane" style={{ height: 60 }} />
        </Form.Item>
        <Form.Item name="lastName" label="Last name" rules={[{ required: true, message: 'Last name is required' }]}>
          <Input size="large" allowClear prefix={<UserOutlined style={{ color: '#8c8c8c' }} />} placeholder="e.g. Doe" style={{ height: 60 }} />
        </Form.Item>
        <Form.Item name="shiftPreferences" label="Shift preferences" style={{ marginBottom: 24 }} rules={[{ required: true, message: 'Please select a shift preference' }]}>
          <ShiftSelector />


        </Form.Item>
        <Form.Item label="Date of birth" validateStatus={dobError ? 'error' : ''} help={dobError || undefined}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input
              ref={yearRef}
              value={dobYear}
              onChange={handleYearChange}
              onKeyDown={handleYearKeyDown}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              placeholder="YYYY"
              size="large"
              allowClear={false}
              style={{ height: 60, textAlign: 'center' }}
            />
            <span style={{ opacity: 0.6 }}>/</span>
            <Input
              ref={monthRef}
              value={dobMonth}
              onChange={handleMonthChange}
              onKeyDown={handleMonthKeyDown}
              onBlur={handleMonthBlur}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              placeholder="MM"
              size="large"
              allowClear={false}
              style={{ height: 60, textAlign: 'center', width: 100 }}
            />
            <span style={{ opacity: 0.6 }}>/</span>
            <Input
              ref={dayRef}
              value={dobDay}
              onChange={handleDayChange}
              onKeyDown={handleDayKeyDown}
              onBlur={handleDayBlur}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              placeholder="DD"
              size="large"
              allowClear={false}
              style={{ height: 60, textAlign: 'center', width: 100 }}
            />
          </div>
        </Form.Item>
        {/* Hidden field to bind composed DOB to form with validation */}
        <Form.Item name="dob" rules={[{ required: true, message: 'Date of birth is required' }]} style={{ display: 'none' }}>
          <Input />
        </Form.Item>


        <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {onBack ? (
            <Button htmlType="button" onClick={onBack} type="text" icon={<ArrowLeftOutlined />} style={{ color: '#667085', fontSize: 16 }}>Back</Button>
          ) : <div />}
          <Button type="primary" htmlType="submit" disabled={!isFormValid} style={{
            height: 48,
            padding: '0 32px',
            fontSize: 16,
            borderRadius: 8,
            backgroundColor: '#004d54',
            borderColor: '#004d54',
            boxShadow: '0 4px 12px rgba(0, 77, 84, 0.15)',
            opacity: !isFormValid ? 0.6 : 1
          }} loading={loading}>
            Continue
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default PersonalInfoStep;


